import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'

import DownloadAreaAgree from '@/pages/DownloadAreaAgree'
import Login from '@/pages/Login'
import Menu from '@/pages/Menu'
import NewOrder from '@/pages/orderFlow/NewOrder'
import OrderHistory from '@/pages/OrderHistory'
import OrderSummary from '@/pages/orderFlow/OrderSummary'
import DeliveryAddress from '@/pages/orderFlow/DeliveryAddress'
import OrderConfirmation from '@/pages/orderFlow/OrderConfirmation'
import OrderSuccess from '@/pages/orderFlow/OrderSuccess'
import OrderFailure from '@/pages/orderFlow/OrderFailure'
import Payment from '@/pages/orderFlow/Payment'
import ViewOrder from '@/pages/ViewOrder'
import ResetPassword from '@/pages/ResetPassword'
import SetPassword from '@/pages/SetPassword'
import StartPage from '@/pages/StartPage'
import StudentRegistration from '@/pages/StudentRegistration'
import StudentVerification from '@/pages/StudentVerification'
import { getDisplayMode } from '@/displayMode'

Vue.use(VueRouter)

const routes = [
  {
    name: 'index',
    path: '/',
    component: StartPage,
    meta: {
      title: 'Händler-Bereich',
      authenticated: null // we don't want any redirect based on authentication
    }
  },
  {
    name: 'login',
    path: '/anmeldung',
    component: Login,
    meta: {
      title: 'Anmeldung',
      authenticated: false
    }
  },
  {
    name: 'reset-password',
    path: '/kennwort-vergessen',
    component: ResetPassword,
    meta: {
      title: 'Kennwort vergessen',
      authenticated: false
    }
  },
  {
    name: 'set-password',
    path: '/kennwort-eingeben',
    component: SetPassword,
    meta: {
      title: 'Kennwort eingeben',
      authenticated: null
    }
  },
  {
    name: 'student-registration',
    path: '/student-registration',
    component: StudentRegistration,
    meta: {
      title: 'Schüler-Registrierung',
      authenticated: false
    }
  },
  {
    name: 'student-verification',
    path: '/student-verification/:token',
    component: StudentVerification,
    meta: {
      title: 'E-Mail Verifizierung',
      authenticated: false
    }
  },
  {
    name: 'menu',
    path: '/menu',
    component: Menu,
    meta: {
      title: 'Menü',
      authenticated: true
    }
  },
  {
    name: 'download-agree',
    path: '/nutzungsbedingungen',
    component: DownloadAreaAgree,
    meta: {
      title: 'Nutzungsbedingungen',
      authenticated: true
    }
  },
  {
    name: 'order-history',
    path: '/meine-bestellungen',
    component: OrderHistory,
    meta: {
      title: 'Meine Bestellungen',
      authenticated: true
    }
  },
  {
    name: 'new-order',
    path: '/neue-bestellung',
    component: NewOrder,
    meta: {
      title: 'Neue Bestellung',
      authenticated: true
    }
  },
  {
    name: 'order-summary',
    path: '/neue-bestellung/ubersicht',
    component: OrderSummary,
    meta: {
      title: 'Neue Bestellung-Übersicht',
      authenticated: true,
      orderFlow: true
    }
  },
  {
    name: 'delivery-address',
    path: '/neue-bestellung/lieferdetails',
    component: DeliveryAddress,
    meta: {
      title: 'Neue Bestellung-Lieferdetails',
      authenticated: true,
      orderFlow: true
    }
  },
  {
    name: 'payment',
    path: '/neue-bestellung/zahlugsverfahren',
    component: Payment,
    meta: {
      title: 'Neue Bestellung-Zahlugsverfahren',
      authenticated: true,
      orderFlow: true
    }
  },
  {
    name: 'order-confirmation',
    path: '/neue-bestellung/bestatigung',
    component: OrderConfirmation,
    meta: {
      title: 'Neue Bestellung-Bestätigung',
      authenticated: true,
      orderFlow: true
    }
  },
  {
    name: 'order-success',
    path: '/neue-bestellung/erfolg',
    component: OrderSuccess,
    meta: {
      title: 'Neue Bestellung-Erfolg',
      authenticated: true
    }
  },
  {
    name: 'order-failure',
    path: '/neue-bestellung/fehler',
    component: OrderFailure,
    meta: {
      title: 'Neue Bestellung-Fehler',
      authenticated: true,
      orderFlow: true
    }
  },
  {
    name: 'view-order',
    path: '/bestellungs-ubersicht',
    component: ViewOrder,
    meta: {
      title: 'Bestellungs-Übersicht',
      authenticated: true
    }
  }
]

const router = new VueRouter({
  routes
  // mode: 'history'
})

router.beforeEach(async (to, from, next) => {
  const displayMode = getDisplayMode()

  // Special handling for student registration mode
  if (displayMode === 'studentRegistration') {
    // Allow access to student registration and verification routes
    if (to.name === 'student-registration' || to.name === 'student-verification') {
      next()
      return
    }
    // Redirect root to student registration in this mode
    if (to.name === 'index') {
      next({ name: 'student-registration', replace: true })
      return
    }
    // Allow authenticated users to access other areas
    if (!store.state.authentication.authenticated) {
      next({ name: 'student-registration', replace: true })
      return
    }
  }

  // trigger authentication if needed
  await store.dispatch('authentication/checkAuthentication')
  // wait if authentication is running still before
  if (store.state.authentication.authenticationPromise) {
    await store.state.authentication.authenticationPromise
  }
  // move from / or /login to /menu automatically if logged in
  if (store.state.authentication.authenticated && to.meta.authenticated === false) {
    next({ name: 'menu', replace: true })
    return
  }
  // prevent access to secure routes
  if (to.meta.authenticated && !store.state.authentication.authenticated) {
    next({ name: 'login', replace: true })
    return
  }
  if (to.meta.orderFlow && !store.getters['orderForm/hasOrdedProducts']) {
    next({ name: 'menu', replace: true })
    return
  }
  if (to.meta.orderFlow && store.state.orderForm.formHasError) {
    next({ name: 'new-order', replace: true })
    return
  }
  // go to requested route
  next()
})

router.afterEach((to, from) => {
  if (document) {
    document.title = to.meta.title ? to.meta.title + ' - Händler-Bereich' : 'Händler-Bereich'
  }
  if (window) {
    window.scrollTo(0, 0)
  }
  if (window._paq) {
    window._paq.push(['setCustomUrl', window.location.href])
    window._paq.push(['setDocumentTitle', document.title])
    window._paq.push(['deleteCustomVariables', 'page'])
    window._paq.push(['trackPageView'])
  }
})

export default router
